import useSoftSetting from "@/hooks/useSoftSetting";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Range } from "react-range";

interface PriceInputProps {
  minValue: number;
  maxValue: number;
  setMinValue: (value: number) => void;
  setMaxValue: (value: number) => void;
}

const PriceInput: React.FC<PriceInputProps> = ({
  minValue,
  maxValue,
  setMinValue,
  setMaxValue,
}) => {
  const STEP = 10000;
  const MIN = 0;
  const { data } = useSoftSetting();
  const [localValues, setLocalValues] = useState<number[]>([
    minValue,
    maxValue,
  ]);
  const MAX = useMemo(() => maxValue + 10000, [maxValue]);

  const isInternalUpdate = useRef(false);
  useEffect(() => {
    if (!isInternalUpdate.current) {
      setLocalValues([minValue, maxValue]);
    }
    isInternalUpdate.current = false;
  }, [minValue, maxValue]);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setLocalValues([minValue, maxValue]);
  }, [minValue, maxValue]);
  const debouncedUpdate = useCallback(
    (newValues: number[]) => {
      isInternalUpdate.current = true;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setMinValue(newValues[0]);
        setMaxValue(newValues[1]);
      }, 600);
    },
    [setMinValue, setMaxValue]
  );

  return (
    <div className="w-full px-4 py-4 num-vazir ltr">
      <div className="relative mb-8">
        <span className="text-sm font-bold text-gray-800 mt-1 flex flex-row-reverse items-center ltr mb-3 ">
          {localValues[0].toLocaleString()} - {localValues[1].toLocaleString()}
          <span className="mr-1">{data?.currencyUnit}</span>
        </span>
        <Range
          step={STEP}
          min={MIN}
          max={MAX}
          values={localValues}
          onChange={(newValues) => {
            setLocalValues(newValues);
            debouncedUpdate(newValues);
          }}
          renderTrack={({ props, children }) => {
            const { ...restProps } = props;
            return (
              <div
                {...restProps}
                className="h-1 bg-gray-300 rounded-md relative mt-3"
              >
                <div
                  className="absolute bg-gray-800 h-1 rounded-md"
                  style={{
                    left: `${((localValues[0] - MIN) / (MAX - MIN)) * 100}%`,
                    width: `${
                      ((localValues[1] - localValues[0]) / (MAX - MIN)) * 100
                    }%`,
                  }}
                />
                {children}
              </div>
            );
          }}
          renderThumb={({ props }) => {
            const { ...restProps } = props;
            return (
              <div
                {...restProps}
                className="w-4 h-4 bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-600"
              />
            );
          }}
        />
      </div>

      <div className="flex justify-between gap-4 mt-10">
        <input
          type="number"
          value={localValues[0]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newValues = [
              Math.max(MIN, Math.min(Number(e.target.value), localValues[1])),
              localValues[1],
            ];
            setLocalValues(newValues);
            debouncedUpdate(newValues);
          }}
          className="text-center w-[100px] px-3 py-2 border bg-gray-200 rounded-lg focus:border-gray-400 focus:outline-none"
        />

        <input
          type="number"
          value={localValues[1]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newValues = [
              localValues[0],
              Math.max(localValues[0], Math.min(Number(e.target.value), MAX)),
            ];
            setLocalValues(newValues);
            debouncedUpdate(newValues);
          }}
          className="text-center w-[100px] px-3 py-2 border bg-gray-200 rounded-lg focus:border-gray-400 focus:outline-none"
        />
      </div>
    </div>
  );
};

export default PriceInput;
